import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Box, Skeleton, TableCell, Theme } from '@mui/material';
import classNames from 'classnames';
import { Ellipsis } from 'lucide-react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { VirtualizedTableTestIds } from '../../../../utils/testIds/VirtualizedTable.testIds';
import { BaseItem, VirtualizedItemTableProps } from './VirtualizedTable.types';
import { useTableScrollerContext } from './scroller/useTableScroller';
import TreeConnector from './tree/TreeConnector';
import TrunkContinuations from './tree/TrunkContinuations';
import useTreeIndent from './tree/hooks/useTreeIndent';
import { NESTED_CHILDREN_SPACING } from './tree/tree.consts';
import { resolveTreeBranch } from './tree/tree.utils';
import { CHECKBOX_COLUMN_WIDTH, COLUMN_GAP, useStyles } from './virtualizedTable.styles';
import {
  buildLinkTo,
  getFirstColumnIndentShift,
  getFirstColumnPaddingLeft,
  getTreeCellOffset,
  trackVisitedListItem,
} from './virtualizedTable.utils';

const VirtualizedItemTable = <T extends BaseItem>({
  item,
  itemIndex,
  onClickAction,
  columns,
  renderParent,
  isLastChild,
  selectionProps,
  isFirstLoading,
  useParentTableCell = true,
  hasHideColumnsButton,
  disableActionColumn = false,
  hrefState,
  isFirstLevelTreeHidden = false,
}: VirtualizedItemTableProps<T>) => {
  const { isParent, isChild, depth, trunkContinuations, isSelected } = item;
  const { hasTreeBranch, trunkContinuations: visibleTrunkContinuations } = resolveTreeBranch({
    isChild,
    depth,
    trunkContinuations,
    isFirstLevelTreeHidden,
  });
  const { disableCheckBox, onToggleSelectRow, parentCheckboxState, onToggleSelectParent } =
    selectionProps ?? {};
  const { classes } = useStyles();
  const { isScrolling } = useTableScrollerContext();

  const handleClickAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClickAction) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onClickAction(event, item);
  };

  const colspan = [!!handleClickAction, !!onToggleSelectRow].filter(Boolean).length;
  const hasRowCheckbox = !!onToggleSelectRow;
  const { trunkLeftOf } = useTreeIndent(getTreeCellOffset({ depth, hasRowCheckbox }));
  const hasStickyColumn = columns.some((c) => c.fixedHorizontalScroll);

  const className = classNames({
    [classes.parentCell]: !isLastChild && isChild,
    [classes.baseTableCellRow]: true,
  });

  if (isFirstLoading) {
    return (
      <TableCell colSpan={columns.length + colspan} className={classes.loadingRow}>
        <Skeleton variant="rectangular" animation="wave" />
      </TableCell>
    );
  }

  if (isParent && !isChild && renderParent && !useParentTableCell) {
    return renderParent({ item, className: classes.parentCell, visibleColumns: columns });
  }

  if (isParent && !isChild && renderParent) {
    if (onToggleSelectParent) {
      return (
        <>
          <TableCell padding="none" className={classNames(className, classes.checkbox)}>
            <Checkbox
              className={classes.disabledCheckbox}
              disabled={disableCheckBox}
              checked={parentCheckboxState === 'checked'}
              indeterminate={parentCheckboxState === 'indeterminate'}
              size="small"
              onClick={onToggleSelectParent}
            />
          </TableCell>
          <TableCell
            colSpan={columns.length + colspan - 1}
            padding="none"
            className={classes.parentCell}
          >
            {renderParent({ item, className: classes.parentCell, visibleColumns: columns })}
          </TableCell>
        </>
      );
    }

    return (
      <TableCell colSpan={columns.length + colspan} padding="none" className={classes.parentCell}>
        {renderParent({ item, className: classes.parentCell, visibleColumns: columns })}
      </TableCell>
    );
  }

  return (
    <>
      {!!onToggleSelectRow && (
        <TableCell
          padding="none"
          className={classNames(className, classes.checkbox, {
            [classes.fixedCellSelected]: isSelected,
            [classes.stickyCheckbox]: hasStickyColumn,
          })}
          sx={(theme) => ({
            ...(isChild && {
              position: 'relative',
              transform: `translateX(${theme.spacing(NESTED_CHILDREN_SPACING * depth)})`,
              color: theme.palette.custom.border.primary,
            }),
          })}
        >
          {hasTreeBranch && <TreeConnector isLastChild={!!isLastChild} left={trunkLeftOf(depth)} />}
          <TrunkContinuations
            trunkContinuations={visibleTrunkContinuations}
            trunkLeftOf={trunkLeftOf}
          />
          <Checkbox
            className={classes.disabledCheckbox}
            disabled={disableCheckBox}
            checked={
              isParent && !!onToggleSelectParent ? parentCheckboxState === 'checked' : isSelected
            }
            indeterminate={
              isParent && !!onToggleSelectParent
                ? parentCheckboxState === 'indeterminate'
                : undefined
            }
            size="small"
            onClick={
              isParent && !!onToggleSelectParent
                ? onToggleSelectParent
                : () => onToggleSelectRow(item)
            }
          />
        </TableCell>
      )}
      {columns.map((column, index) => {
        const isFirstColumn = index === 0;
        const component = column.render(item, itemIndex);

        const indentShift = isFirstColumn
          ? getFirstColumnIndentShift({ row: item, hasRowCheckbox })
          : 0;

        const contentPaddingLeft = (theme: Theme) =>
          theme.spacing(
            isFirstColumn ? getFirstColumnPaddingLeft({ row: item, hasRowCheckbox }) : COLUMN_GAP,
          );

        return (
          <TableCell
            className={classNames(className, classes.bodyCell, {
              [classes.tableCellClickable]: component.href || component.onClick,
              [classes.fixedCell]: column.fixedHorizontalScroll,
              [classes.fixedCellSelected]: column.fixedHorizontalScroll && isSelected,
              [classes.fixedScrolledCell]: column.fixedHorizontalScroll && isScrolling,
              [classes.fixedRightCell]: column.fixedRight,
              [classes.fixedRightCellSelected]: column.fixedRight && isSelected,
              [classes.fixedRightScrolledCell]: column.fixedRight && isScrolling,
            })}
            key={column.name}
            width={column.width}
            onClick={component.onClick}
            test-id={`${VirtualizedTableTestIds.COLUMN_PREFIX}${column.name}`}
            sx={(theme) => ({
              minWidth: column.width,
              maxWidth: column.width,
              ...(column.fixedHorizontalScroll &&
                !!onToggleSelectRow && { left: CHECKBOX_COLUMN_WIDTH }),
              ...(indentShift > 0 && {
                position: 'relative',
                transform: `translateX(${theme.spacing(indentShift)})`,
                '& > *': {
                  maxWidth: `calc(100% - ${theme.spacing(indentShift)})`,
                },
              }),
              ...(isFirstColumn && isChild && !onToggleSelectRow && { position: 'relative' }),
            })}
          >
            {isFirstColumn && hasTreeBranch && !onToggleSelectRow && (
              <Box
                component="span"
                sx={(theme) => ({ color: theme.palette.custom.border.primary })}
              >
                <TreeConnector isLastChild={!!isLastChild} left={trunkLeftOf(depth)} />

                <TrunkContinuations
                  trunkContinuations={visibleTrunkContinuations}
                  trunkLeftOf={trunkLeftOf}
                />
              </Box>
            )}
            {!!component.href ? (
              <Box
                component={Link}
                className={classNames(classes.cellContent, classes.bodyCellContent)}
                draggable={false}
                sx={(theme) => ({ paddingLeft: contentPaddingLeft(theme) })}
                to={buildLinkTo(component.href, hrefState)}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  if (event.detail > 0 && window.getSelection()?.toString()) {
                    event.preventDefault();
                    return;
                  }
                  trackVisitedListItem(hrefState, item.id);
                }}
              >
                {component.node}
              </Box>
            ) : (
              <Box
                className={classNames(classes.cellContent, classes.bodyCellContent)}
                sx={(theme) => ({ paddingLeft: contentPaddingLeft(theme) })}
              >
                {component.node}
              </Box>
            )}
          </TableCell>
        );
      })}

      {!!onClickAction && (
        <TableCell
          padding="checkbox"
          className={classNames(className, classes.actionsColumn)}
          align="center"
        >
          <IconButton
            className={classNames({
              [classes.actionsIconButton]: !disableActionColumn,
              [classes.disabledActionsIconButton]: disableActionColumn,
            })}
            icon={Ellipsis}
            onClick={handleClickAction}
            disabled={disableActionColumn}
            testId={VirtualizedTableTestIds.ACTION_BUTTON}
          />
        </TableCell>
      )}

      {!onClickAction && hasHideColumnsButton && (
        <TableCell className={classNames(classes.emptyFixedColumn, className)} />
      )}
    </>
  );
};

const MemoizedComponent = memo(VirtualizedItemTable) as typeof VirtualizedItemTable;

export default MemoizedComponent;
